/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import {Spinner} from '@volkswagen-onehub/components-core';
import * as React from 'react';
import BodyText from '../../components/BodyText';
import Div from '../../components/Div.styled';
import Icon from '../../components/Icon';
import SpaceLike from '../../components/Space';
import TypographyBody from '../../components/Typography/TypographyBody';
import TypographyHeading from '../../components/Typography/TypographyHeading';
import {config} from '../../environment';
import useMediaQuery from '../../hooks/useMediaQuery';
import {useResultStore} from '../../store/responses';
import {useSalesForceObjectStore} from '../../store/salesforce';
import {ResultQuestions, ResultVehicle} from '../../types/questionManuals';
import {TradeInEvaluation} from '../../types/salesforce';
import {sendAnalyticPayload} from '../../utils/analytic';
import useQuestionStore from '../../store/navigation';

export default function QuestionManualResult({
  handleNextStep,
  results,
  handleResetQuestion,
}: {
  handleNextStep: () => void;
  results: ResultQuestions[];
  handleResetQuestion: () => void;
}) {
  const {isMobile} = useMediaQuery();
  const {setTradeInEvaluation} = useSalesForceObjectStore();
  const {updateQuestion} = useResultStore();
  const [vehicle, setVehicle] = React.useState<ResultVehicle | null>(null);
  const [tradeInEvaluation, setTradeInEvaluationState] =
    React.useState<TradeInEvaluation | null>(null);
  const [loading, setLoading] = React.useState(true);
  const {currentQuestion} = useQuestionStore();

  const makeQuestion = results?.find((item) => item.id === 'varient');

  React.useEffect(() => {
    const fetchState = async () => {
      try {
        if (makeQuestion && makeQuestion.makeCode) {
          const specificationResponse = await fetch(
            `${config.API_REDBOOK_SPECIFICATION_URL}`,
            {
              method: 'POST',
              body: JSON.stringify({
                makecode: makeQuestion.makeCode,
                familycode: makeQuestion.familyCode,
                year: parseInt(makeQuestion.year as string),
                month: parseInt(makeQuestion.month as string),
                rbc: makeQuestion.rbc,
              }),
              headers: {
                'Content-Type': 'application/json',
              },
              mode: 'cors',
            },
          );

          const result: {data: ResultVehicle} =
            await specificationResponse.json();
          if (result.data) {
            const transformedData = result.data;
            setVehicle(transformedData);
            setTradeInEvaluationState(
              convertTradeInEvaluation(
                result.data as unknown as TradeInEvaluation,
              ),
            );
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchState();
  }, [makeQuestion, results]);

  function convertTradeInEvaluation(
    data: TradeInEvaluation,
  ): TradeInEvaluation {
    const make = results.find((item) => item.id === 'make')?.value;
    const model = results.find((item) => item.id === 'model')?.value;

    const variant = results.find((item) => item.id === 'varient')?.value;
    return {
      ...data,
      make: make || '',
      model: model || '',
      variant: variant || '',
    };
  }

  const renderField = (id: string, value: string) => {
    if (value) {
      return (
        <TypographyBody
          key={id}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            margin: 0,
            fontSize: 16,
            fontWeight: 400,
          }}
        >
          <Icon
            icon={
              (getIcon(id) as
                | 'car-private'
                | 'info'
                | 'chevron-down'
                | 'chevron-up'
                | 'arrow-back'
                | 'arrow-right-alt'
                | 'calendar'
                | 'engine'
                | 'transmission') || 'car-private'
            }
            color="white"
            style={{cursor: 'pointer', marginRight: 16}}
          />
          {value}
        </TypographyBody>
      );
    }
    return null;
  };

  const getIcon = (id: string) => {
    const iconMap: Record<string, string> = {
      carPrivate: 'car-private',
      engine: 'engine',
      calendar: 'calendar',
      transmission: 'transmission',
    };

    return iconMap[id] || '';
  };

  return (
    <Div
      customStyles={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '100%',
        paddingBottom: 40,
      }}
    >
      <Div margin="0 0 60px 0">
        <TypographyHeading tag="h3" bold size="medium" textAlign="center">
          {vehicle &&
            `${vehicle?.makecode} ${vehicle?.familycode} ${vehicle?.badge} ${vehicle?.year}`}
        </TypographyHeading>
      </Div>
      {loading ? (
        <Div width="100%" customStyles={{textAlign: 'center'}}>
          <Spinner variant="large" />
        </Div>
      ) : (
        <Div
          customStyles={{
            height: '100%',
            width: '100%',
            maxWidth: '400px',
          }}
        >
          <SpaceLike
            width="100%"
            direction="vertical"
            size={32}
            customStyles={{
              border: '1px solid #fff',
              borderRadius: 8,
              maxWidth: 400,
              padding: '40px 32px',
              width: '100%',
              marginBottom: '24px',
            }}
          >
            {renderField('calendar', vehicle?.year || '')}
            {renderField('transmission', vehicle?.geartype || '')}
            {renderField('engine', vehicle?.fueltype || '')}
            {renderField(
              'carPrivate',
              vehicle?.bodyconfig || vehicle?.bodystyle || '',
            )}
          </SpaceLike>
          <BodyText size="16px">
            Something isn’t right?{' '}
            <span
              style={{
                textDecorationSkipInk: 'none',
                cursor: 'pointer',
                display: isMobile ? 'block' : 'inline',
              }}
              className="button-text"
              onClick={handleResetQuestion}
            >
              Select your vehicle manually.
            </span>
          </BodyText>
        </Div>
      )}
      <Div customStyles={{textAlign: 'center'}}>
        <button
          style={{
            width: 285,
            height: 48,
            borderRadius: 40,
            border: '2px solid var(--1-white-000, #FFF)',
            backgroundColor: 'transparent',
            margin: '40px 0',
            color: 'white',
            cursor: 'pointer',
          }}
          onClick={() => {
            handleNextStep();
            setTradeInEvaluation(tradeInEvaluation as TradeInEvaluation);
            updateQuestion({
              id: 'q2_your_vehicle',
              value: vehicle?.description || '',
            });

            sendAnalyticPayload({
              process: {StepName: currentQuestion, StepNumber: '1'},
              product: {
                Manufacturer: tradeInEvaluation?.make || '',
                Name: tradeInEvaluation?.familycode || '',
              },
            });
          }}
        >
          Yes, please continue
        </button>
      </Div>
    </Div>
  );
}
